import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../App';

const OrgContext = createContext(null);
const STORAGE_KEY = 'orka-selected-org';
const API = '/api';

export const useOrg = () => {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used within OrgProvider');
  return ctx;
};

export const OrgProvider = ({ children }) => {
  const { user } = useAuth();
  const [orgId, setOrgId] = useState(null);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [orgList, setOrgList] = useState([]);
  const [needsOrgSelection, setNeedsOrgSelection] = useState(false);
  const [orgLoading, setOrgLoading] = useState(true);

  const selectOrg = useCallback((id) => {
    localStorage.setItem(STORAGE_KEY, id);
    setOrgId(id);
    setNeedsOrgSelection(false);
    const found = orgList.find(o => o.org_id === id);
    if (found) setCurrentOrg(found);
  }, [orgList]);

  // Resolve org when user changes
  useEffect(() => {
    if (!user) { setOrgLoading(false); return; }

    const membershipOrgIds = user.membership_org_ids || [];

    if (membershipOrgIds.length === 0) {
      setOrgId(null);
      setCurrentOrg(null);
      setNeedsOrgSelection(false);
      setOrgLoading(false);
      return;
    }

    if (membershipOrgIds.length === 1) {
      // Single org — auto-select, no ambiguity
      const singleId = membershipOrgIds[0];
      setOrgId(singleId);
      localStorage.setItem(STORAGE_KEY, singleId);
      // Fetch org details
      fetch(`${API}/org/current?org_id=${singleId}`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setCurrentOrg(d); })
        .catch(() => {})
        .finally(() => setOrgLoading(false));
      return;
    }

    // Multiple orgs — check localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && membershipOrgIds.includes(stored)) {
      setOrgId(stored);
      // Fetch org list + current org details in parallel
      Promise.all([
        fetch(`${API}/org/my-organizations`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/org/current?org_id=${stored}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      ]).then(([list, org]) => {
        setOrgList(list);
        if (org) setCurrentOrg(org);
      }).catch(() => {}).finally(() => setOrgLoading(false));
      return;
    }

    // No valid stored selection — need user to choose
    fetch(`${API}/org/my-organizations`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        setOrgList(list);
        setNeedsOrgSelection(true);
      })
      .catch(() => {})
      .finally(() => setOrgLoading(false));
  }, [user]);

  // Handle 409 from any downstream call — trigger org selection
  const handle409 = useCallback(() => {
    if (!needsOrgSelection && orgList.length > 0) {
      setNeedsOrgSelection(true);
    } else if (orgList.length === 0) {
      // Fetch org list if we don't have it yet
      fetch(`${API}/org/my-organizations`, { credentials: 'include' })
        .then(r => r.ok ? r.json() : [])
        .then(list => { setOrgList(list); setNeedsOrgSelection(true); })
        .catch(() => {});
    }
  }, [needsOrgSelection, orgList]);

  return (
    <OrgContext.Provider value={{ orgId, currentOrg, orgList, orgLoading, needsOrgSelection, selectOrg, handle409 }}>
      {children}
    </OrgContext.Provider>
  );
};
